import { useEffect, useRef, useCallback, useState } from 'react'
import { notifyManager, useQueryClient, type Query, type QueryClient } from '@tanstack/react-query'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { useAuthStore } from '@/store/authStore'
import { refreshAccessToken } from '@/api/client'  // #328
import { liveLog, needsTokenRefresh, type LiveStatus } from '@/lib/liveLink'  // #328
import type { Event as EventRecord } from '@/types'  // #332

interface WebSocketMessage {
  type: string
  event?: any
  message?: string
  event_ids?: number[] // #406 — suspicious_seen
  timestamp?: string
}

interface UseWebSocketOptions {
  enabled?: boolean
  onMessage?: (message: WebSocketMessage) => void
  onError?: (error: Event) => void
  /** #328 — the server said connection_established again after a drop (not on the first open of the page). */
  onReconnected?: () => void
  /** #328 — the first connection_established of the page (or of a new sign-in): the provider starts its replay marker. */
  onFirstConnected?: () => void
}

function getWebSocketURL(): string {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL
  }

  const apiUrl = import.meta.env.VITE_API_URL || window.location.origin + '/api'
  let wsUrl = apiUrl
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://')
    .replace(/\/api\/?$/, '')

  return `${wsUrl}/ws/notifications/`
}

/**
 * #294 redesign (Review Focus 2) — put a critical WebSocket event into the open-critical list at once, as
 * an Event-shaped placeholder: the payload has no created_at / acknowledged_at / age, occurred_at may be
 * null, and the camera is `camera_id`. The bar, popup, tab title and sound then carry the alert even while
 * the list request fails (an API restart); the refetch that follows replaces the placeholder. Deduped by id.
 */
function seedCriticalOpen(queryClient: QueryClient, payload: Record<string, any>) {
  if (typeof payload.id !== 'number') return
  const placeholder = {
    ...payload,
    camera: payload.camera ?? payload.camera_id,
    occurred_at: payload.occurred_at ?? new Date().toISOString(),
    acknowledged_at: null,
    acknowledged_by: null,
    _seeded: true,
  }
  queryClient.setQueryData(queryKeys.events.criticalOpen(), (old: any) => {
    if (!old || !Array.isArray(old.results)) return { count: 1, next: null, previous: null, results: [placeholder] }
    if (old.results.some((e: { id: number }) => e.id === payload.id)) return old
    return { ...old, count: (old.count ?? old.results.length) + 1, results: [placeholder, ...old.results] }
  })
}

/**
 * #406 — the sibling for a suspicious event: into the dashboard block's own list at once (never criticalOpen, which
 * feeds the critical sound controller), as an Event-shaped placeholder; the refetch that follows replaces it. Deduped by id.
 */
function seedSuspiciousOpen(queryClient: QueryClient, payload: Record<string, any>) {
  if (typeof payload.id !== 'number') return
  const placeholder = {
    ...payload,
    camera: payload.camera ?? payload.camera_id,
    occurred_at: payload.occurred_at ?? new Date().toISOString(),
    seen_at: null,
    seen_by: null,
    _seeded: true,
  }
  queryClient.setQueryData(queryKeys.events.suspiciousOpen(), (old: any) => {
    if (!old || !Array.isArray(old.results)) return { count: 1, next: null, previous: null, results: [placeholder] }
    if (old.results.some((e: { id: number }) => e.id === payload.id)) return old
    return { ...old, count: (old.count ?? old.results.length) + 1, results: [placeholder, ...old.results] }
  })
}

/** #406 — someone pressed Seen: those events leave the block at once. Each one leaves once, also when this tab's own
 *  optimistic mark already took it out of what the block shows, so the count never drops twice for one event. */
function pruneSuspiciousOpen(queryClient: QueryClient, ids: unknown) {
  if (!Array.isArray(ids) || ids.length === 0) return
  queryClient.setQueryData(queryKeys.events.suspiciousOpen(), (old: any) => {
    if (!old || !Array.isArray(old.results)) return old
    const results = old.results.filter((e: { id: number }) => !ids.includes(e.id))
    if (results.length === old.results.length) return old
    const dropped = old.results.length - results.length
    return { ...old, count: Math.max(results.length, (old.count ?? old.results.length) - dropped), results }
  })
}

/** #332 — the task handler's word on a video's playable copy (WebSocket `event_updated`, sent through Django). */
export interface EventUpdate {
  id: number
  video_status: 'processing' | 'ready' | 'failed'
  /** The presigned link of the playable copy; null when there is none (failed) or Django could not sign it. */
  detection_video_url: string | null
}

const VIDEO_STATUSES: ReadonlySet<string> = new Set(['processing', 'ready', 'failed'])

/** #332 — an `event_updated` payload that can be applied, or null (no numeric id, an unknown status). */
function readEventUpdate(payload: unknown): EventUpdate | null {
  const p = payload as { id?: unknown; video_status?: unknown; detection_video_url?: unknown } | null | undefined
  if (!p || typeof p.id !== 'number' || typeof p.video_status !== 'string' || !VIDEO_STATUSES.has(p.video_status)) {
    return null
  }
  const link = typeof p.detection_video_url === 'string' && p.detection_video_url !== '' ? p.detection_video_url : null
  return { id: p.id, video_status: p.video_status as EventUpdate['video_status'], detection_video_url: link }
}

interface EventPage { results: EventRecord[]; [k: string]: unknown }
interface InfiniteEvents { pages: EventPage[]; [k: string]: unknown }
const isEventPage = (v: unknown): v is EventPage =>
  !!v && typeof v === 'object' && Array.isArray((v as { results?: unknown }).results)
const isInfiniteEvents = (v: unknown): v is InfiniteEvents =>
  !!v && typeof v === 'object' && Array.isArray((v as { pages?: unknown }).pages)

/**
 * #332 — change one event in place in every cached events list, as #292 marks a row read: the recent rows, every
 * page of every Events page and Favourites list, and the open critical alerts (the Dashboard pins those rows, and
 * its popup follows them). video_status always; the link only where the row has none yet (#329: the first link
 * seen is the one that plays, and another signature for the same clip would restart a playing card). No refetch.
 * A list without the event, or whose copy already says the same, is left alone. A patch is not a fetch: each list
 * keeps its fetch time and, when it was marked for a refetch (a newer event), stays marked, so it still refetches
 * when it is next shown. Returns how many lists changed.
 */
export function applyEventUpdate(queryClient: QueryClient, update: EventUpdate): number {
  const patchRow = (e: EventRecord): EventRecord => {
    if (e?.id !== update.id) return e
    const addLink = update.detection_video_url !== null && !e.detection_video_url
    if (e.video_status === update.video_status && !addLink) return e
    return {
      ...e,
      video_status: update.video_status,
      ...(addLink ? { detection_video_url: update.detection_video_url } : {}),
    }
  }
  const patchPage = (page: EventPage): EventPage => {
    const results = page.results.map(patchRow)
    return results.some((e, i) => e !== page.results[i]) ? { ...page, results } : page
  }
  const patchData = (data: unknown): unknown => {
    if (isInfiniteEvents(data)) {
      const pages = data.pages.map((p) => (isEventPage(p) ? patchPage(p) : p))
      return pages.some((p, i) => p !== data.pages[i]) ? { ...data, pages } : undefined
    }
    if (isEventPage(data)) {
      const page = patchPage(data)
      return page !== data ? page : undefined
    }
    return undefined
  }
  let changed = 0
  notifyManager.batch(() => {
    for (const query of queryClient.getQueryCache().findAll({ queryKey: ['events'] })) {
      const next = patchData(query.state.data)
      if (next === undefined) continue
      const { dataUpdatedAt, isInvalidated } = query.state
      queryClient.setQueryData(query.queryKey, next, { updatedAt: dataUpdatedAt })
      if (isInvalidated) query.invalidate()
      changed++
    }
  })
  return changed
}

const HEARTBEAT_INTERVAL = 30000
const BASE_RECONNECT_DELAY = 3000
const MAX_RECONNECT_DELAY = 30000
// #328 — this many failed attempts in a row (closed before connection_established) and the status says
// offline; it keeps retrying.
const OFFLINE_AFTER_FAILED_ATTEMPTS = 5
// #328 — refresh the access token before connecting when it expires within this margin. The server checks it
// only when a socket connects and refuses an expired one before accepting (Daphne answers HTTP 403), so the
// browser sees nothing but a 1006 close, the same as a network failure.
const TOKEN_REFRESH_MARGIN_MS = 60_000
const ABNORMAL_CLOSE = 1006

/** #328 (round 4, R5) — the provider's recent-events query has just asked, or holds an answer younger than its stale
 *  time: the first open of the page must not ask again (one events request per page load). */
function recentRowsFreshOrFetching(queryClient: QueryClient): boolean {
  const state = queryClient.getQueryState(queryKeys.events.recent())
  if (!state) return false
  if (state.fetchStatus === 'fetching') return true
  return state.data !== undefined && !state.isInvalidated
    && Date.now() - state.dataUpdatedAt < queryOptions.events.recent.staleTime
}

/** #336 — the same rule for any list at the first open: on its way, or an answer younger than its stale time. */
function freshOrFetching(query: Query, staleTime: number): boolean {
  const state = query.state
  if (state.fetchStatus === 'fetching') return true
  return state.data !== undefined && !state.isInvalidated && Date.now() - state.dataUpdatedAt < staleTime
}

/** #336 — an Events page list holding at most one loaded page (#329's rule: a long scrolled list is not pulled again;
 *  an infinite-query refetch re-fetches every loaded page, one after the other). */
function onePageOrLess(query: Query): boolean {
  return ((query.state.data as { pages?: unknown[] } | undefined)?.pages?.length ?? 1) <= 1
}

/** #328 — the status while not connected: offline when the browser has no network or after 5 failed attempts. */
function downStatus(failedAttempts: number): LiveStatus {
  const noNetwork = typeof navigator !== 'undefined' && navigator.onLine === false
  return noNetwork || failedAttempts >= OFFLINE_AFTER_FAILED_ATTEMPTS ? 'offline' : 'reconnecting'
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { enabled = true, onMessage, onError, onReconnected, onFirstConnected } = options

  const wsRef = useRef<WebSocket | null>(null)
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const shouldConnectRef = useRef(enabled)
  // #328 — the watchdog (when the last message came, when the last ping went out); failed attempts in a row (for
  // offline); a connection_established was seen in this session (the next one is a reconnect); a token refresh
  // before connecting is in flight; the expired-token fast path was used since the last working connection.
  const lastMessageAtRef = useRef(0)
  const pingSentAtRef = useRef(0)
  const failedAttemptsRef = useRef(0)
  const hadLiveRef = useRef(false)
  const refreshingTokenRef = useRef(false)
  const expiredFastPathUsedRef = useRef(false)
  // #328 — the current socket has said connection_established (an 'online' after a blip can say live again)
  const liveSocketRef = useRef(false)
  // #328 — timers and the token refresh reach connect() through this ref (they are made before it exists)
  const connectRef = useRef<(options?: { tokenChecked?: boolean }) => void>(() => {})

  // Keep latest callbacks in refs so connect() never captures a stale closure
  const onMessageRef = useRef(onMessage)
  const onErrorRef = useRef(onError)
  const onReconnectedRef = useRef(onReconnected)
  const onFirstConnectedRef = useRef(onFirstConnected)
  useEffect(() => { onMessageRef.current = onMessage }, [onMessage])
  useEffect(() => { onErrorRef.current = onError }, [onError])
  useEffect(() => { onReconnectedRef.current = onReconnected }, [onReconnected])
  useEffect(() => { onFirstConnectedRef.current = onFirstConnected }, [onFirstConnected])

  const [isConnected, setIsConnected] = useState(false)
  const [lastEvent, setLastEvent] = useState<any>(null)
  // #328 — where the live connection stands, and when a working one was last lost
  const [status, setStatus] = useState<LiveStatus>('connecting')
  const [lastDisconnectAt, setLastDisconnectAt] = useState<number | null>(null)

  const queryClient = useQueryClient()

  const clearHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
  }, [])

  const clearReconnect = useCallback(() => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current)
      reconnectRef.current = null
    }
  }, [])

  // #328 — drop the current socket without waiting for the browser's close event (on a dead link that can take
  // about a minute) and connect again at once: the watchdog found it dead, or the provider's poll found an event
  // it never delivered (deaf). Its handlers go first, so nothing it does later can reach this hook.
  const restart = useCallback((reason: string) => {
    const ws = wsRef.current
    if (!ws || !shouldConnectRef.current) return
    ws.onopen = null
    ws.onmessage = null
    ws.onerror = null
    ws.onclose = null
    clearHeartbeat()
    wsRef.current = null
    liveSocketRef.current = false
    setIsConnected(false)
    setLastDisconnectAt(Date.now())
    failedAttemptsRef.current = 0
    setStatus(downStatus(0))
    try { ws.close(4000, reason) } catch { /* already closing */ }
    reconnectAttemptsRef.current = 0
    clearReconnect()
    liveLog('reconnect', { attempt: 0, trigger: reason })
    connectRef.current()
  }, [clearHeartbeat, clearReconnect])

  const scheduleReconnect = useCallback(() => {
    // Exponential backoff: 3s, 6s, 12s, 24s, 30s (capped), 30s, ...
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
      MAX_RECONNECT_DELAY
    )
    reconnectAttemptsRef.current++
    console.log(`[WebSocket] Reconnecting in ${delay / 1000}s (attempt ${reconnectAttemptsRef.current})...`)
    liveLog('reconnect', { attempt: reconnectAttemptsRef.current, delayMs: delay })
    clearReconnect()
    reconnectRef.current = setTimeout(() => connectRef.current(), delay)
  }, [clearReconnect])

  // #328 — refresh the access token, then connect with the one the store holds. A refresh with no answer (network,
  // timeout, 5xx) keeps the session (the user stays signed in) and the backoff goes on; a refused refresh token
  // signs out (the API client does it) and there is nothing to connect with.
  const refreshThenConnect = useCallback((reason: 'pre-connect' | 'closed-expired') => {
    refreshingTokenRef.current = true
    refreshAccessToken({ keepSessionOnTransportError: true }).then(
      () => {
        refreshingTokenRef.current = false
        liveLog('auth-refresh', { reason, ok: true })
        connectRef.current({ tokenChecked: true })
      },
      () => {
        refreshingTokenRef.current = false
        liveLog('auth-refresh', { reason, ok: false })
        if (!shouldConnectRef.current) return
        failedAttemptsRef.current++
        setStatus(downStatus(failedAttemptsRef.current))
        scheduleReconnect()
      },
    )
  }, [scheduleReconnect])

  const connect = useCallback(({ tokenChecked = false }: { tokenChecked?: boolean } = {}) => {
    if (!shouldConnectRef.current) return

    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }
    if (refreshingTokenRef.current) return   // #328 — it connects when the refresh is done

    const token = useAuthStore.getState().accessToken
    if (!token) {
      console.log('[WebSocket] No access token available, skipping connection')
      return
    }

    // #328 — an expired (or about to expire) token would be refused: refresh it first, once per connect
    if (!tokenChecked && needsTokenRefresh(token, Date.now(), TOKEN_REFRESH_MARGIN_MS)) {
      refreshThenConnect('pre-connect')
      return
    }

    try {
      const wsUrl = getWebSocketURL()
      const wsUrlWithToken = `${wsUrl}?token=${encodeURIComponent(token)}`
      console.log('[WebSocket] Connecting to:', wsUrl)
      const ws = new WebSocket(wsUrlWithToken)
      // #328 — this socket opened / reached connection_established (a close before that is a failed attempt)
      let opened = false
      let established = false

      // #328 — the watchdog found it dead
      const dropDead = () => {
        liveLog('pong-timeout', { silentMs: Date.now() - lastMessageAtRef.current })
        restart('pong timeout')
      }

      // #328 — every handler ignores a socket that has been replaced (a fast reconnect can open a new one while
      // this one is still closing): its late close must not stop the new heartbeat or schedule a reconnect.
      ws.onopen = () => {
        if (wsRef.current !== ws) return
        console.log('[WebSocket] Connected')
        liveLog('open')
        opened = true
        lastMessageAtRef.current = Date.now()
        pingSentAtRef.current = 0
        setIsConnected(true)
        reconnectAttemptsRef.current = 0
        clearReconnect()
        // #294 — a critical event broadcast while the socket was down (or before it first opened) is not
        // replayed: refresh the open-critical list (alert bar, popups, tab title, sound) and the recent rows.
        // #328 (round 4, R5) — the recent rows: a reconnect refetches them (the provider's replay reads that
        // answer: one request per reconnect); the first open of the page only when the provider's own first
        // request is neither on its way nor fresh (one request per page load).
        queryClient.invalidateQueries({ queryKey: queryKeys.events.criticalOpen() })
        if (hadLiveRef.current || !recentRowsFreshOrFetching(queryClient)) {
          queryClient.invalidateQueries({ queryKey: queryKeys.events.recent() })
        }
        // #336 — the map pins and the Events page lists missed the same broadcasts: a reconnect refreshes them too
        // (the lists only while they hold one page); the first open only what is neither on its way nor fresh (the
        // page that mounted it has just asked: one request per page load, as for the recent rows above).
        const reconnected = hadLiveRef.current
        // #406 — the Suspicious block missed the same broadcasts, and any Seen sent while the socket was down
        queryClient.invalidateQueries({
          queryKey: queryKeys.events.suspiciousOpen(),
          predicate: (q) => reconnected || !freshOrFetching(q, queryOptions.events.suspiciousOpen.staleTime),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.cameras.mapPins(),
          predicate: (q) => reconnected || !freshOrFetching(q, queryOptions.cameras.mapPins.staleTime),
        })
        queryClient.invalidateQueries({
          queryKey: ['events', 'paginated'],
          predicate: (q) => onePageOrLess(q)
            && (reconnected || !freshOrFetching(q, queryOptions.events.paginated.staleTime)),
        })

        // Heartbeat to keep connection alive; #328 — and the watchdog: nothing at all came back since the previous
        // ping (a pong, a notification: anything counts), so the connection is dead even if the browser still says
        // OPEN (NAT timeout, laptop sleep, a proxy dropping idle sockets). It counts pings, not seconds: a hidden
        // tab's interval can run only about once a minute, and a late tick alone must not look like a dead link.
        clearHeartbeat()
        heartbeatRef.current = setInterval(() => {
          if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return
          if (pingSentAtRef.current > lastMessageAtRef.current) {
            dropDead()
            return
          }
          pingSentAtRef.current = Date.now()
          ws.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }))
        }, HEARTBEAT_INTERVAL)
      }

      ws.onmessage = (event: MessageEvent) => {
        if (wsRef.current !== ws) return
        lastMessageAtRef.current = Date.now()   // #328 — any message is a sign of life, a pong included
        try {
          const message: WebSocketMessage = JSON.parse(event.data)

          if (message.type === 'pong') return

          if (message.type === 'connection_established') {
            console.log('[WebSocket] Connection established')
            reconnectAttemptsRef.current = 0
            // #328 — live; after a drop the provider replays what this tab missed
            established = true
            liveSocketRef.current = true
            failedAttemptsRef.current = 0
            expiredFastPathUsedRef.current = false
            setStatus('live')
            const reconnected = hadLiveRef.current
            hadLiveRef.current = true
            try {
              if (reconnected) onReconnectedRef.current?.()
              else onFirstConnectedRef.current?.()
            } catch (error) { console.error('[WebSocket] Replay failed:', error) }
            return
          }

          if (message.type === 'notification' && message.event) {
            console.log('[WebSocket] New event notification:', message.event)
            setLastEvent(message.event)
            queryClient.invalidateQueries({ queryKey: queryKeys.events.recent() })
            queryClient.invalidateQueries({ queryKey: queryKeys.events.unreadCount() }) // #292
            queryClient.invalidateQueries({ queryKey: queryKeys.events.total() }) // #304
            // #078 addendum: invalidate the EventsPage infinite query so an open list
            // refreshes immediately on a new event (partial-prefix matches all filter variants).
            queryClient.invalidateQueries({ queryKey: ['events', 'paginated'] })
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unread() })
            queryClient.invalidateQueries({ queryKey: queryKeys.cameras.all() })
            // #247 — the fleet map's pins have their own key; refresh them too.
            queryClient.invalidateQueries({ queryKey: queryKeys.cameras.mapPins() })
            // #294 — a critical event: the pinned rows, the alert bar, the popups, the tab title and the sound
            if (message.event.is_critical === true) {
              seedCriticalOpen(queryClient, message.event)
              queryClient.invalidateQueries({ queryKey: queryKeys.events.criticalOpen() })
            }
            // #406 — a suspicious event: the dashboard's block. The announcement itself is a normal event's (the provider)
            if (message.event.level === 'suspicious') {
              seedSuspiciousOpen(queryClient, message.event)
              queryClient.invalidateQueries({ queryKey: queryKeys.events.suspiciousOpen() })
            }
          }

          // #294 — someone acknowledged critical events (here, in another tab, or another user): every
          // open dashboard clears its red rows, alert bar, popups, tab title and sound.
          if (message.type === 'critical_acknowledged') {
            queryClient.invalidateQueries({ queryKey: queryKeys.events.criticalOpen() })
            queryClient.invalidateQueries({ queryKey: queryKeys.events.recent() })
            queryClient.invalidateQueries({ queryKey: ['events', 'paginated'] })
            // #392 — Favourites shows the red critical cards too (its own key, no poll)
            queryClient.invalidateQueries({ queryKey: ['events', 'favourites'] })
          }

          // #406 — someone pressed Seen on suspicious events (here, in another tab, or another user): every open dashboard
          // clears those rows from its block, and the lists that show them learn who saw them. Quiet: nothing is announced.
          if (message.type === 'suspicious_seen') {
            pruneSuspiciousOpen(queryClient, message.event_ids)
            queryClient.invalidateQueries({ queryKey: queryKeys.events.suspiciousOpen() })
            queryClient.invalidateQueries({ queryKey: queryKeys.events.recent() })
            queryClient.invalidateQueries({ queryKey: ['events', 'paginated'] })
            queryClient.invalidateQueries({ queryKey: ['events', 'favourites'] })
          }

          // #332 — the handler's word on a video's playable copy: ready (with its link, or null when Django could not
          // sign one) or failed. That event changes in place in every cached list; the rows and an open popup follow
          // (the push replaces #329's timed list refreshes; the 30 s poll stays the safety net). A cache patch, never
          // an announcement: no refetch, no toast, and not handed to onMessage (the provider's announcer, #328).
          if (message.type === 'event_updated') {
            const update = readEventUpdate(message.event)
            if (update) {
              liveLog('event-updated', {
                id: update.id, status: update.video_status, hasLink: update.detection_video_url !== null,
              })
              applyEventUpdate(queryClient, update)
            }
            return
          }

          onMessageRef.current?.(message)
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error)
        }
      }

      ws.onerror = (error) => {
        if (wsRef.current !== ws) return
        console.error('[WebSocket] Error:', error)
        try { onErrorRef.current?.(error) } catch { /* ignore handler errors */ }
      }

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return
        console.log('[WebSocket] Disconnected:', event.code, event.reason)
        liveLog('close', { code: event.code, reason: event.reason })
        liveSocketRef.current = false
        setIsConnected(false)
        clearHeartbeat()
        // #328 — a working connection was lost (not an attempt that never opened)
        if (opened) setLastDisconnectAt(Date.now())
        failedAttemptsRef.current = established ? 0 : failedAttemptsRef.current + 1

        if (shouldConnectRef.current) {
          setStatus(downStatus(failedAttemptsRef.current))

          // #328 — closed while the token has already expired (a refused token looks like this: 1006): do not
          // wait for the backoff; refresh and reconnect at once. Once, until a connection works again (a clock
          // far ahead would make every new token look expired).
          const current = useAuthStore.getState().accessToken
          if (event.code === ABNORMAL_CLOSE && !expiredFastPathUsedRef.current && current
              && needsTokenRefresh(current, Date.now(), 0)) {
            expiredFastPathUsedRef.current = true
            clearReconnect()
            reconnectAttemptsRef.current = 0
            refreshThenConnect('closed-expired')
            return
          }

          scheduleReconnect()
        }
      }

      wsRef.current = ws
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error)
      setIsConnected(false)
    }
  }, [queryClient, clearHeartbeat, clearReconnect, refreshThenConnect, scheduleReconnect, restart])
  useEffect(() => { connectRef.current = connect }, [connect])

  const disconnect = useCallback(() => {
    shouldConnectRef.current = false
    clearHeartbeat()
    clearReconnect()

    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    setIsConnected(false)
    // #328 — a later sign-in starts a new session: its first open is not a reconnect
    hadLiveRef.current = false
    liveSocketRef.current = false
    failedAttemptsRef.current = 0
    expiredFastPathUsedRef.current = false
    setStatus('offline')
  }, [clearHeartbeat, clearReconnect])

  useEffect(() => {
    shouldConnectRef.current = enabled

    if (enabled) {
      setStatus('connecting')
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  // #328 — the network came back, or the tab came to the front: a socket that is not open or opening
  // reconnects now instead of waiting out the backoff (up to 30 s). The network went away: say offline at once
  // (the socket may still look OPEN until the watchdog finds it dead); if it comes back while that socket is
  // still live (a short blip), say live again.
  useEffect(() => {
    const reconnectNow = (trigger: 'online' | 'visible') => {
      if (!shouldConnectRef.current) return
      const state = wsRef.current?.readyState
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return
      clearReconnect()
      reconnectAttemptsRef.current = 0
      failedAttemptsRef.current = 0
      setStatus((current) => (current === 'offline' ? downStatus(0) : current))
      liveLog('reconnect', { attempt: 0, trigger })
      connect()
    }
    const onOnline = () => {
      if (!shouldConnectRef.current) return
      const open = wsRef.current?.readyState === WebSocket.OPEN
      if (open && liveSocketRef.current) setStatus('live')
      else setStatus((current) => (current === 'offline' ? downStatus(0) : current))
      reconnectNow('online')
    }
    const onOffline = () => { if (shouldConnectRef.current) setStatus('offline') }
    const onVisibility = () => { if (document.visibilityState === 'visible') reconnectNow('visible') }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [connect, clearReconnect])

  return {
    isConnected,
    lastEvent,
    status,
    lastDisconnectAt,
    restart,  // #328
    reconnect: connect,
    disconnect,
  }
}
